import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { ArrowLeftRight, BookOpen, Coins, Library as LibraryIcon, RefreshCw, Trophy } from "lucide-react";

import { DecisionsCard, DeskTiles, ViewerNote, type DeskTile } from "../../components/dashboard/DeskParts";
import { longDay, shortDay } from "../../components/dashboard/deskLogic";
import { circulationTotals, lateness, overdueConfig, ugx, viewerNote, type LibrarianDesk, type OverdueLoan } from "../../components/librarian/librarianLogic";
import { ErrorState, LoadingState } from "../../components/PageState";
import { ClientRegister, StatusDot, type RegisterColumn } from "../../components/register/ClientRegister";
import { api } from "../../services/api";

/**
 * The librarian's dashboard: what needs doing today, the overdue list by
 * class (for class teachers to chase), the fortnight's lending, the titles
 * in demand and the reading programmes running. Lending itself happens on
 * the Library page.
 */
export default function LibrarianDashboard() {
  const query = useQuery({
    queryKey: ["librarian-desk"],
    queryFn: async () => (await api.get("/dashboard/librarian/")).data as LibrarianDesk,
    refetchInterval: 60_000,
  });

  if (query.isLoading) return <LoadingState label="Loading the librarian's dashboard…" />;
  if (query.isError || !query.data) return <ErrorState title="Dashboard unavailable" message="The librarian's dashboard could not be loaded. Check your connection and try again." retry={() => query.refetch()} />;

  const data = query.data;
  const figures = data.figures;
  const circulation = circulationTotals(data.circulation);
  const busiestDay = Math.max(1, ...data.circulation.map((row) => row.lent + row.returned));

  const tiles: DeskTile[] = [
    { label: "On the shelf", value: `${figures.on_shelf} of ${figures.copies}`, note: `${figures.titles} titles in the catalogue`, link: "/library", icon: LibraryIcon },
    { label: "Out on loan", value: String(figures.out), note: figures.overdue ? `${figures.overdue} overdue` : "None overdue", link: "/library?tab=loans&view=out", icon: BookOpen, alert: figures.overdue > 0 },
    { label: "Today", value: `${figures.lent_today} out · ${figures.returned_today} back`, note: "Lent and returned today", link: "/library?tab=loans", icon: ArrowLeftRight },
    { label: "Unpaid fines", value: ugx(figures.unpaid_fines), note: "Overdue, damage and lost books", link: "/library?tab=fines&view=pending", icon: Coins, alert: Number(figures.unpaid_fines) > 0 },
  ];

  const columns: RegisterColumn<OverdueLoan>[] = [
    { header: "Borrower", cell: (row) => <span><span className="block font-medium text-gray-900">{row.borrower}</span><span className="text-xs text-gray-500">{row.admission_number}</span></span>, exportValue: (row) => row.borrower },
    { header: "Class", cell: (row) => <span><span className="block">{row.class_name || "—"}</span>{row.class_teacher && <span className="text-xs text-gray-500">{row.class_teacher}</span>}</span>, exportValue: (row) => row.class_name },
    { header: "Class teacher", cell: () => "", exportValue: (row) => row.class_teacher, exportOnly: true },
    { header: "Book", cell: (row) => <span><span className="block text-gray-900">{row.title}</span><span className="text-xs text-gray-500">Copy {row.copy}</span></span>, exportValue: (row) => `${row.title} (copy ${row.copy})` },
    { header: "Due", cell: (row) => <span className="whitespace-nowrap">{shortDay(row.due)}</span>, exportValue: (row) => row.due },
    { header: "Late", cell: (row) => <StatusDot {...lateness(row.days_late)} />, exportValue: (row) => row.days_late, align: "right" },
    { header: "Fine so far", cell: (row) => ugx(row.fine_to_date), exportValue: (row) => Number(row.fine_to_date), align: "right" },
  ];

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Library</h1>
          <p className="mt-1 text-sm text-gray-500">{longDay(data.today)} · lending, reservations and fines at a glance.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/library?tab=loans" className="btn btn-primary">Lend or take back a book</Link>
          <button type="button" onClick={() => query.refetch()} className="btn btn-outline gap-2">
            <RefreshCw className={`h-4 w-4 ${query.isFetching ? "animate-spin" : ""}`} /> Refresh
          </button>
        </div>
      </div>

      <ViewerNote text={viewerNote(data.viewer_role)} />
      <DecisionsCard decisions={data.work} title="Work waiting" where="in the library" />
      <DeskTiles tiles={tiles} />

      <ClientRegister
        title="Overdue books by class"
        description="Every book still out past its due date, in class order, with the fine it has run up at UGX 1,000 a day. Export it for the class teachers to chase."
        actions={<Link to="/library?tab=loans&view=overdue" className="btn btn-outline h-9 text-sm">Open loans</Link>}
        rows={data.overdue}
        config={overdueConfig}
        columns={columns}
        rowKey={(row) => row.id}
        noun={["book", "books"]}
        exportName={{ file: `library-overdue-${data.today}`, title: `Overdue library books, ${longDay(data.today)}` }}
        emptyText="Nothing is overdue."
      />

      <div className="grid gap-6 lg:grid-cols-3">
        <section className="card">
          <div className="card-titlebar">
            <div><h2 className="font-semibold text-gray-900">The last two weeks</h2><p className="text-xs text-gray-500">{circulation.lent} lent · {circulation.returned} returned</p></div>
          </div>
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500"><th className="px-6 py-2 font-medium">Day</th><th className="px-2 py-2 text-right font-medium">Lent</th><th className="px-2 py-2 text-right font-medium">Back</th><th className="px-6 py-2"><span className="sr-only">Activity</span></th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {[...data.circulation].reverse().map((row) => (
                <tr key={row.date} className={row.lent + row.returned ? "" : "text-gray-400"}>
                  <td className="px-6 py-1.5">{shortDay(row.date)}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{row.lent}</td>
                  <td className="px-2 py-1.5 text-right tabular-nums">{row.returned}</td>
                  <td className="px-6 py-1.5"><span className="block h-1.5 w-full overflow-hidden rounded-full bg-gray-100"><span className="block h-full rounded-full bg-primary-500" style={{ width: `${((row.lent + row.returned) / busiestDay) * 100}%` }} /></span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="card">
          <div className="card-titlebar"><div><h2 className="font-semibold text-gray-900">In demand</h2><p className="text-xs text-gray-500">Most borrowed in the last 90 days</p></div><Link to="/library" className="text-xs font-medium text-primary-700 hover:underline">Catalogue</Link></div>
          {data.popular.length ? (
            <ol className="divide-y divide-gray-100">
              {data.popular.map((book, index) => (
                <li key={book.id} className="flex items-center justify-between gap-3 px-6 py-2.5 text-sm">
                  <span className="flex min-w-0 items-center gap-3"><span className="w-4 text-right text-xs tabular-nums text-gray-400">{index + 1}</span><span className="truncate text-gray-900">{book.title}</span></span>
                  <span className="shrink-0 text-xs tabular-nums text-gray-500">{book.loans} {book.loans === 1 ? "loan" : "loans"}</span>
                </li>
              ))}
            </ol>
          ) : <p className="px-6 py-6 text-center text-sm text-gray-500">Nothing has been borrowed in the last 90 days.</p>}
        </section>

        <section className="card">
          <div className="card-titlebar"><h2 className="flex items-center gap-2 font-semibold text-gray-900"><Trophy className="h-4 w-4 text-gray-500" /> Reading programmes</h2><Link to="/library?tab=programmes" className="text-xs font-medium text-primary-700 hover:underline">Programmes</Link></div>
          {data.programmes.length ? (
            <ul className="divide-y divide-gray-100">
              {data.programmes.map((programme) => (
                <li key={programme.id} className="px-6 py-2.5 text-sm">
                  <span className="block font-medium text-gray-900">{programme.name}</span>
                  <span className="text-xs text-gray-500">{programme.program_type} · ends {shortDay(programme.end_date)} · {programme.readers} {programme.readers === 1 ? "reader" : "readers"}, {programme.finished} finished</span>
                </li>
              ))}
            </ul>
          ) : <p className="px-6 py-6 text-center text-sm text-gray-500">No reading programme is running.</p>}
        </section>
      </div>
    </div>
  );
}
